<template>
    <div
        @mouseenter="isButtonHovered = true"
        @mouseleave="isButtonHovered = false">
        <pendo-button
            v-if="!iconOnly"
            class="ask-pendo-context-button"
            theme="app"
            type="secondary"
            :label="isLeoRebrand ? $t('askPendo.Ask Leo') : $t('askPendo.Ask agent mode')"
            :disabled="disabled"
            pendo-feature="ask-pendo-context-button"
            @click="openConversationDrawer">
            <leo-sparkle-icon
                circle-hidden
                :spinning="!disabled && isButtonHovered" />
            <span>{{ isLeoRebrand ? $t('askPendo.Ask Leo') : $t('askPendo.Ask agent mode') }}</span>
        </pendo-button>
        <pendo-button
            v-else
            v-pendo-tooltip="isLeoRebrand ? $t('askPendo.Ask Leo') : $t('askPendo.Ask agent mode')"
            theme="app"
            type="tertiary"
            :disabled="disabled"
            :tooltip="isLeoRebrand ? $t('askPendo.Ask Leo') : $t('askPendo.Ask agent mode')"
            pendo-feature="ask-pendo-context-button"
            @click="openConversationDrawer">
            <leo-sparkle-icon :spinning="!disabled && isButtonHovered" />
        </pendo-button>
    </div>
</template>

<script>
import { PendoButton } from '@pendo/components';
import LeoSparkleIcon from '@/ask-pendo/components/leo-sparkle-icon.vue';
import { mapActions } from 'vuex';
import { v4 as uuid } from 'uuid';
import { getInContextPrompt } from '../utils/in-context-prompt-utils';
import { openAgentModePanel } from '@/vue-router/agent-mode-panel-url.utils';

export default {
    name: 'AskPendoContextButton',
    components: {
        LeoSparkleIcon,
        PendoButton,
    },
    props: {
        /**
         * Metadata describing the context in which the conversation is taking place.
         */
        context: {
            type: Object,
            default: () => ({}),
        },
        countableType: {
            type: String,
            default: '',
        },
        countableId: {
            type: String,
            default: '',
        },
        countableName: {
            type: String,
            default: '',
        },
        suggestedQuestions: {
            type: Array,
            default: () => [],
        },
        iconOnly: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        /** When provided, called on click to get conversation id instead of creating with prompt. Enables custom flows (e.g. table artifact). */
        getConversationId: {
            type: Function,
            default: null,
        },
    },
    data () {
        return {
            isButtonHovered: false,
        };
    },
    computed: {
        isLeoRebrand () {
            return this.$segmentFlag('leoRebrand');
        },
    },
    methods: {
        ...mapActions('askPendo', ['createConversation']),
        async openConversationDrawer () {
            let conversationId;
            if (this.getConversationId) {
                conversationId = await this.getConversationId();
            } else {
                const prompt = this.generatePrompt();
                conversationId = uuid();
                const artifacts = {};
                for (const question of this.suggestedQuestions) {
                    artifacts[uuid()] = {
                        type: 'next_actions',
                        action_type: 'ask_question',
                        data: {
                            question,
                            prompt,
                        },
                    };
                }
                const metadata = {
                    heading_type: 'ask_question',
                    artifact_ids: Object.keys(artifacts),
                };
                await this.createConversation({
                    id: conversationId,
                    prompt,
                    metadata,
                    artifacts,
                    user_context: this.countableName
                        ? { type: 'countable', displayName: this.countableName }
                        : this.context,
                });
            }

            if (conversationId) {
                openAgentModePanel(this.$router, this.$route, conversationId, {
                    paramKey: this.isLeoRebrand ? 'leo' : 'agent-mode',
                });
            }
        },
        generatePrompt () {
            return getInContextPrompt(this.countableType, this.countableId, this.countableName);
        },
    },
};
</script>

<style scoped lang="less">
.ask-pendo-context-button {
    border: 1px solid transparent;
    border-radius: 3px; /* Works with border-radius */
    background:
        linear-gradient(var(--palette-gray-0), var(--palette-gray-0)) padding-box,
        linear-gradient(to right, var(--palette-purple-70), var(--palette-teal-60)) border-box;
    background-origin: border-box;
    background-clip: padding-box, border-box;

    :deep(> span) {
        display: inline-flex;
        align-items: center;
        gap: 4px;
    }
}
</style>
